import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { ScriptValueDialog, variablesToFieldsInfo } from '@microservices/wiskey-react-components'
import { IconPickerField, VectorIcon } from '@microservices/wiskey-react-components'
import { ICON_REGISTRY } from '@mobileConstants'
import { ColorPickerField } from '@components/ColorPicker'
import {
  ElementStyle,
  FieldElement,
  IconElement,
  StyleParameters,
  TemplateElement,
  ViewElement,
} from '@mobileTypes'
import { Code as CodeIcon } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'

import { useFetchAllDisplayRulesQuery, useFetchDisplayRuleByIdQuery } from '@redux/api/displayRules.api'
import { useFetchMultipleObjectsByCodeQuery } from '@redux/api/objects.api'

import { FIELD_VALUE_TYPE, FORMATS_DATE_OPTIONS } from '@constants'
import { ObjectFieldDTO, ShortDisplayRule } from '@types'
import {
  ModalRefPathPicker,
  type RefPathValue,
} from '@libraryComponents/ModalRefPathPicker/ModalRefPathPicker'

export type TemplateElementConfig = TemplateElement

interface TemplateElementConfigDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  initialData?: Partial<TemplateElement>
  commands: Array<{
    commandKey: string
    commandName: string
    objectCode: string
    objectTitle: string
  }>
  onClose: () => void
  onSave: (config: TemplateElementConfig) => void
}

interface FormData {
  type: 'view' | 'icon' | 'field'
  style?: ElementStyle
  isJS?: boolean
  size?: number
  iconValue?: string | VectorIcon
  displayRule?: ShortDisplayRule | null
  useDisplayRule?: boolean
  useStyleParameters?: boolean
  styleParameters?: StyleParameters
  code?: string
  title?: string
  value?: string
  objectCode?: string
  bindType?: 'field' | 'js'
  selectedObjectCode?: string | null
  field?: ObjectFieldDTO | null
  pathArray?: RefPathValue['pathArray'] | null
  formatDate?: string | null
}

export const TemplateElementConfigDialog: FC<TemplateElementConfigDialogProps> = ({
  open,
  mode,
  initialData,
  commands,
  onClose,
  onSave,
}) => {
  const [showJsEditor, setShowJsEditor] = useState(false)
  const [tabValue, setTabValue] = useState(0)

  const templateElementScriptVariables = useMemo(
    () => [{ name: 'item', type: 'any', description: 'Current item from the data source' }],
    []
  )

  const { control, handleSubmit, reset, watch, setValue } = useForm<FormData>({
    defaultValues: {
      type: 'view',
      style: {
        flexDirection: 'column',
        flex: undefined,
        gap: undefined,
      },
      isJS: false,
      size: 36,
      iconValue: '',
      code: '',
      title: '',
      value: '',
      objectCode: '',
      bindType: 'field',
      displayRule: null,
      useDisplayRule: false,
      useStyleParameters: false,
      styleParameters: {
        color: undefined,
        backgroundColor: undefined,
        borderWidth: undefined,
        borderColor: undefined,
        borderRadius: undefined,
        padding: undefined,
        margin: undefined,
        fontSize: undefined,
      },
      selectedObjectCode: null,
      field: null,
      pathArray: null,
      formatDate: null,
    },
  })

  const elementType = watch('type')
  const isJS = watch('isJS')
  const iconValue = watch('iconValue')
  const bindType = watch('bindType')
  const useDisplayRule = watch('useDisplayRule')
  const useStyleParameters = watch('useStyleParameters')
  const watchObjectCode = watch('selectedObjectCode')
  const watchField = watch('field')
  const watchPathArray = watch('pathArray')
  const watchFormatDate = watch('formatDate')

  const isDateTimeField =
    bindType === 'field' && watchField?.valueType === FIELD_VALUE_TYPE.DATETIME

  // Создаем список уникальных объектов из команд
  const availableObjects = useMemo(() => {
    const objectMap = new Map<string, { objectCode: string; objectTitle: string }>()
    commands.forEach(cmd => {
      if (cmd.objectCode && !objectMap.has(cmd.objectCode)) {
        objectMap.set(cmd.objectCode, {
          objectCode: cmd.objectCode,
          objectTitle: cmd.objectTitle || cmd.objectCode,
        })
      }
    })

    return Array.from(objectMap.values())
  }, [commands])

  // Загружаем объекты для всех команд
  const objectCodes = useMemo(() => commands.map(c => c.objectCode), [commands])
  const { data: objectModels = [] } = useFetchMultipleObjectsByCodeQuery(objectCodes, {
    skip: objectCodes.length === 0,
  })

  // Опции для селектора полей (зависит от выбранного объекта)
  const fieldOptions = useMemo(() => {
    if (!watchObjectCode) return []
    const objectModel = objectModels.find(model => model.code === watchObjectCode)

    return objectModel?.fields || []
  }, [watchObjectCode, objectModels])

  // Загружаем все DisplayRules без фильтрации (для всех типов элементов: field, icon, view)
  const { data: displayRulesData = [], isLoading: isLoadingDisplayRules } =
    useFetchAllDisplayRulesQuery(
      {
        objectCode: undefined,
        objectField: undefined,
      },
      {
        skip: !useDisplayRule,
      }
    )

  // Получаем ID выбранного DisplayRule для загрузки полной версии
  const selectedDisplayRuleId = watch('displayRule')?.id

  // Загружаем полный DisplayRule при выборе
  const { data: fullDisplayRule, isLoading: isLoadingFullDisplayRule } =
    useFetchDisplayRuleByIdQuery(
      { id: selectedDisplayRuleId as number },
      {
        skip: !selectedDisplayRuleId || !useDisplayRule,
      }
    )

  // Создаем опции с добавлением текущего displayRule из формы
  const displayRules = useMemo(() => {
    // Клонируем, чтобы не мутировать данные из кэша
    const options: ShortDisplayRule[] = displayRulesData ? [...displayRulesData] : []

    // Добавляем displayRule из watch(), если редактируем и его нет в списке
    const currentDisplayRule = watch('displayRule')
    if (currentDisplayRule && currentDisplayRule.id) {
      const existsInOptions = options.some(opt => opt.id === currentDisplayRule.id)
      if (!existsInOptions) {
        options.unshift(currentDisplayRule)
      }
    }

    // Также добавляем displayRule из initialData, если его нет в загруженных опциях
    if (initialData?.displayRule && initialData.displayRule.id) {
      const existsInOptions = options.some(opt => opt.id === initialData.displayRule.id)
      if (!existsInOptions && (!currentDisplayRule || currentDisplayRule.id !== initialData.displayRule.id)) {
        options.unshift(initialData.displayRule)
      }
    }

    return options
  }, [displayRulesData, initialData?.displayRule, watch])

  useEffect(() => {
    if (open && initialData) {
      const data: FormData = {
        type: initialData.type || 'view',
      }

      if (initialData.type === 'view') {
        const viewEl = initialData as ViewElement
        data.style = viewEl.style
        // Convert mobile DisplayRule to ShortDisplayRule
        data.displayRule = viewEl.displayRule
          ? ({
              id: viewEl.displayRule.id || null,
              code: viewEl.displayRule.code,
              title: viewEl.displayRule.title,
              objectCode: viewEl.displayRule.objectCode,
              objectTitle: viewEl.displayRule.objectTitle,
              objectField: undefined,
            } as ShortDisplayRule)
          : null
        data.useDisplayRule = !!viewEl.displayRule
        data.useStyleParameters = viewEl.useStyleParameters || false
        data.styleParameters = viewEl.styleParameters || {
          color: undefined,
          backgroundColor: undefined,
          borderWidth: undefined,
          borderColor: undefined,
          borderRadius: undefined,
          padding: undefined,
          margin: undefined,
          fontSize: undefined,
        }
      } else if (initialData.type === 'icon') {
        const iconEl = initialData as IconElement
        data.isJS = iconEl.isJS
        data.size = iconEl.size
        // Try to parse value as JSON (VectorIcon object), otherwise use as string
        try {
          const parsed = JSON.parse(iconEl.value)
          if (parsed && typeof parsed === 'object' && parsed.type && parsed.value) {
            data.iconValue = parsed as VectorIcon
          } else {
            data.iconValue = iconEl.value
          }
        } catch {
          // Not valid JSON, use as string
          data.iconValue = iconEl.value
        }
        // Convert mobile DisplayRule to ShortDisplayRule
        data.displayRule = iconEl.displayRule
          ? ({
              id: iconEl.displayRule.id || null,
              code: iconEl.displayRule.code,
              title: iconEl.displayRule.title,
              objectCode: iconEl.displayRule.objectCode,
              objectTitle: iconEl.displayRule.objectTitle,
              objectField: undefined,
            } as ShortDisplayRule)
          : null
        data.useDisplayRule = !!iconEl.displayRule
        data.useStyleParameters = iconEl.useStyleParameters || false
        data.styleParameters = iconEl.styleParameters || {
          color: undefined,
          backgroundColor: undefined,
          borderWidth: undefined,
          borderColor: undefined,
          borderRadius: undefined,
          padding: undefined,
          margin: undefined,
          fontSize: undefined,
        }
      } else if (initialData.type === 'field') {
        const fieldEl = initialData as FieldElement
        data.code = fieldEl.code
        data.title = fieldEl.title || ''
        data.value = fieldEl.value
        data.objectCode = fieldEl.objectCode
        data.bindType = fieldEl.bindType
        data.formatDate = fieldEl.formatDate || null
        // Convert mobile DisplayRule to ShortDisplayRule
        data.displayRule = fieldEl.displayRule
          ? ({
              id: fieldEl.displayRule.id || null,
              code: fieldEl.displayRule.code,
              title: fieldEl.displayRule.title,
              objectCode: fieldEl.displayRule.objectCode,
              objectTitle: fieldEl.displayRule.objectTitle,
              objectField: undefined,
            } as ShortDisplayRule)
          : null
        data.useDisplayRule = !!fieldEl.displayRule
        data.useStyleParameters = fieldEl.useStyleParameters || false
        data.styleParameters = fieldEl.styleParameters || {
          color: undefined,
          backgroundColor: undefined,
          borderWidth: undefined,
          borderColor: undefined,
          borderRadius: undefined,
          padding: undefined,
          margin: undefined,
          fontSize: undefined,
        }

        // Если это field bindType, установим selectedObjectCode
        if (fieldEl.bindType === 'field' && fieldEl.objectCode) {
          data.selectedObjectCode = fieldEl.objectCode
          // Преобразуем pathArray из PathArrayItem[] в EmbeddedObject[] для формы
          if (fieldEl.pathArray && fieldEl.pathArray.length > 0) {
            data.pathArray = fieldEl.pathArray.map(item => ({
              objectCode: item.objectCode || null,
              field: item.field || null,
              refValueType: item.valueType as any,
            })) as any
          } else {
            data.pathArray = null
          }
        }
      }

      reset(data)
    }
  }, [open, initialData, commands, reset])

  // Находим поле после загрузки objectModels
  useEffect(() => {
    if (
      open &&
      initialData?.type === 'field' &&
      (initialData as FieldElement).bindType === 'field' &&
      watchObjectCode &&
      fieldOptions.length > 0
    ) {
      const fieldEl = initialData as FieldElement

      // Если есть pathArray, используем его для восстановления поля
      if (fieldEl.pathArray && fieldEl.pathArray.length > 0) {
        const lastPath = fieldEl.pathArray[fieldEl.pathArray.length - 1]
        if (lastPath?.field) {
          const field = fieldOptions.find(f => f.name === lastPath.field)
          if (field) {
            setValue('field', field)
          }
        }
      } else {
        // Иначе ищем по code
        const field = fieldOptions.find(
          f => f.name === fieldEl.code || (f as any).code === fieldEl.code
        )
        if (field) {
          setValue('field', field)
        }
      }
    }
  }, [open, initialData, watchObjectCode, fieldOptions, setValue])

  // Сбрасываем formatDate если поле не datetime или bindType изменился на js
  useEffect(() => {
    if (bindType === 'js' || (bindType === 'field' && !isDateTimeField)) {
      setValue('formatDate', null)
    }
  }, [bindType, isDateTimeField, setValue])

  const onSubmit = (data: FormData) => {
    let result: TemplateElementConfig

    // Используем полный DisplayRule если он загружен, иначе используем данные из формы
    const displayRuleToSave = data.useDisplayRule
      ? (fullDisplayRule || data.displayRule)
      : undefined

    if (data.type === 'view') {
      result = {
        type: 'view',
        style: data.style || { flexDirection: 'column' },
        children: initialData?.type === 'view' ? (initialData as ViewElement).children : [],
        displayRule: displayRuleToSave
          ? {
              id: displayRuleToSave.id || 0,
              code: displayRuleToSave.code || '',
              title: displayRuleToSave.title || '',
              objectCode: String(displayRuleToSave.objectCode || ''),
              objectTitle: displayRuleToSave.objectTitle || '',
              objectField: displayRuleToSave.objectField || undefined,
              isJs: displayRuleToSave.isJs || false,
              jsSetting: displayRuleToSave.jsSetting || undefined,
              displayRuleValues: displayRuleToSave.displayRuleValues || null,
            }
          : undefined,
        useStyleParameters: data.useStyleParameters || false,
        styleParameters: data.useStyleParameters ? data.styleParameters : undefined,
      }
    } else if (data.type === 'icon') {
      const iconVal = data.iconValue
      let iconSize: number | undefined
      let iconValueStr: string
      let iconColor: string | undefined

      // Если не JS и iconValue - это VectorIcon объект, сохраняем как JSON строку
      if (!data.isJS && typeof iconVal === 'object' && iconVal !== null) {
        const vectorIcon = iconVal as VectorIcon
        iconSize = vectorIcon.size
        iconColor = vectorIcon.color
        // Сохраняем полный объект VectorIcon как JSON строку для сохранения type
        iconValueStr = JSON.stringify({
          type: vectorIcon.type,
          value: vectorIcon.value,
          size: vectorIcon.size,
          ...(iconColor && { color: iconColor }),
        })
      } else if (typeof iconVal === 'string') {
        // Если это уже строка, используем её
        iconValueStr = iconVal
        iconSize =
          (initialData?.type === 'icon' ? (initialData as IconElement).size : undefined) || 36
      } else {
        // Для обратной совместимости используем размер из initialData или значение по умолчанию
        iconSize =
          (initialData?.type === 'icon' ? (initialData as IconElement).size : undefined) || 36
        iconValueStr = ''
      }

      result = {
        type: 'icon',
        isJS: data.isJS,
        size: iconSize,
        value: iconValueStr,
        ...(iconColor && { color: iconColor }),
        displayRule: displayRuleToSave
          ? {
              id: displayRuleToSave.id || 0,
              code: displayRuleToSave.code || '',
              title: displayRuleToSave.title || '',
              objectCode: String(displayRuleToSave.objectCode || ''),
              objectTitle: displayRuleToSave.objectTitle || '',
              objectField: displayRuleToSave.objectField || undefined,
              isJs: displayRuleToSave.isJs || false,
              jsSetting: displayRuleToSave.jsSetting || undefined,
              displayRuleValues: displayRuleToSave.displayRuleValues || null,
            }
          : undefined,
        useStyleParameters: data.useStyleParameters || false,
        styleParameters: data.useStyleParameters ? data.styleParameters : undefined,
      }
    } else {
      // field
      if (data.bindType === 'field') {
        // Определяем поле из pathArray или data.field
        let fieldName = data.code || ''
        let fieldValueType = null
        let fieldType: 'object_link' | 'single' = 'single'
        let columnToSort: string | null = null

        if (data.pathArray && data.pathArray.length > 0) {
          // Используем последний элемент pathArray для определения поля
          const lastPath = data.pathArray[data.pathArray.length - 1]
          fieldName = lastPath?.field || fieldName
          columnToSort = fieldName

          // Если есть data.field, используем его для valueType и fieldType
          if (data.field) {
            fieldValueType = data.field.valueType || null
            fieldType = (data.field.type as any) === 'LINK' ? 'object_link' : 'single'
          }
        } else if (data.field) {
          // Используем данные из выбранного поля
          fieldName = data.field.name || fieldName
          fieldValueType = data.field.valueType || null
          fieldType = (data.field.type as any) === 'LINK' ? 'object_link' : 'single'
          columnToSort = data.field.name || null
        }

        // Field bindType - используем данные из выбранного поля или pathArray
        result = {
          type: 'field',
          code: fieldName,
          title: data.title || '',
          value: fieldName,
          objectCode: data.selectedObjectCode || data.objectCode || '',
          valueType: fieldValueType,
          fieldType: fieldType,
          bindType: 'field',
          formatDate: data.formatDate || null,
          useDisplayRule: data.useDisplayRule || false,
          displayRule: displayRuleToSave
            ? {
                id: displayRuleToSave.id || 0,
                code: displayRuleToSave.code || '',
                title: displayRuleToSave.title || '',
                objectCode: String(displayRuleToSave.objectCode || ''),
                objectTitle: displayRuleToSave.objectTitle || '',
                objectField: displayRuleToSave.objectField || undefined,
                isJs: displayRuleToSave.isJs || false,
                jsSetting: displayRuleToSave.jsSetting || undefined,
                displayRuleValues: displayRuleToSave.displayRuleValues || null,
              }
            : undefined,
          // Дополнительные поля из DEFAULT_ROW_ELEMENT
          pinnedColumn: null,
          objectValue: null,
          pathArray: data.pathArray
            ? data.pathArray.map(item => ({
                objectCode: item.objectCode || '',
                field: item.field || '',
                valueType: item.refValueType || '',
                isPk: false,
                isInternalId: false,
                model: null,
              }))
            : null,
          actions: [],
          columnToSort: columnToSort,
          editable: true,
          enumDescription: false,
          visibility: true,
          useLocalParameters: false,
          isPk: null,
          parameters: [],
          restrictions: null,
          valueFormat: null,
          preFill: null,
          useStyleParameters: data.useStyleParameters || false,
          styleParameters: data.useStyleParameters ? data.styleParameters : undefined,
        }
      } else {
        // JS bindType
        result = {
          type: 'field',
          code: data.code || '',
          title: data.title || '',
          value: data.value || '',
          objectCode: data.objectCode || '',
          valueType: null,
          fieldType: 'single',
          bindType: 'js',
          formatDate: null,
          useDisplayRule: data.useDisplayRule || false,
          displayRule: displayRuleToSave
            ? {
                id: displayRuleToSave.id || 0,
                code: displayRuleToSave.code || '',
                title: displayRuleToSave.title || '',
                objectCode: String(displayRuleToSave.objectCode || ''),
                objectTitle: displayRuleToSave.objectTitle || '',
                objectField: displayRuleToSave.objectField || undefined,
                isJs: displayRuleToSave.isJs || false,
                jsSetting: displayRuleToSave.jsSetting || undefined,
                displayRuleValues: displayRuleToSave.displayRuleValues || null,
              }
            : undefined,
          // Дополнительные поля из DEFAULT_ROW_ELEMENT
          pinnedColumn: null,
          objectValue: null,
          pathArray: null,
          actions: [],
          columnToSort: null,
          editable: true,
          enumDescription: false,
          visibility: true,
          useLocalParameters: false,
          isPk: null,
          parameters: [],
          restrictions: null,
          valueFormat: null,
          preFill: null,
          useStyleParameters: data.useStyleParameters || false,
          styleParameters: data.useStyleParameters ? data.styleParameters : undefined,
        }
      }
    }

    onSave(result)
    onClose()
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()
    handleSubmit(onSubmit)(event)
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <form onSubmit={handleFormSubmit}>
          <DialogTitle>
            {mode === 'create' ? 'Add Template Element' : 'Edit Template Element'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name='type'
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth disabled={mode === 'edit'}>
                      <InputLabel>Element Type</InputLabel>
                      <Select {...field} label='Element Type'>
                        <MenuItem value='view'>View (Container)</MenuItem>
                        <MenuItem value='icon'>Icon</MenuItem>
                        <MenuItem value='field'>Field</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* VIEW Configuration */}
              {elementType === 'view' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant='subtitle2' gutterBottom>
                      Layout Style
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name='style.flexDirection'
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Flex Direction</InputLabel>
                          <Select {...field} label='Flex Direction'>
                            <MenuItem value='row'>Row</MenuItem>
                            <MenuItem value='column'>Column</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name='style.gap'
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          onChange={e =>
                            field.onChange(
                              e.target.value === '' ? undefined : Number(e.target.value)
                            )
                          }
                          fullWidth
                          label='Gap'
                          type='number'
                          helperText='Space between children'
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name='style.flex'
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          onChange={e =>
                            field.onChange(
                              e.target.value === '' ? undefined : Number(e.target.value)
                            )
                          }
                          fullWidth
                          label='Flex'
                          type='number'
                          helperText='Flex grow/shrink value'
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name='style.justifyContent'
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Justify Content</InputLabel>
                          <Select {...field} label='Justify Content'>
                            <MenuItem value=''>None</MenuItem>
                            <MenuItem value='flex-start'>Flex Start</MenuItem>
                            <MenuItem value='center'>Center</MenuItem>
                            <MenuItem value='flex-end'>Flex End</MenuItem>
                            <MenuItem value='space-between'>Space Between</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name='style.alignItems'
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Align Items</InputLabel>
                          <Select {...field} label='Align Items'>
                            <MenuItem value=''>None</MenuItem>
                            <MenuItem value='flex-start'>Flex Start</MenuItem>
                            <MenuItem value='center'>Center</MenuItem>
                            <MenuItem value='flex-end'>Flex End</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name='useDisplayRule'
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={e => field.onChange(e.target.checked)}
                            />
                          }
                          label='Use Display Rule'
                        />
                      )}
                    />

                    {useDisplayRule && (
                      <Controller
                        name='displayRule'
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            value={field.value}
                            onChange={(_, newValue) => field.onChange(newValue)}
                            options={displayRules}
                            getOptionLabel={option => `${option.title} (${option.objectCode})`}
                            loading={isLoadingDisplayRules}
                            renderInput={params => (
                              <TextField
                                {...params}
                                label='Display Rule'
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {isLoadingDisplayRules ? (
                                        <CircularProgress color='inherit' size={20} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name='useStyleParameters'
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={e => field.onChange(e.target.checked)}
                            />
                          }
                          label='Use Style Parameters'
                        />
                      )}
                    />

                    {useStyleParameters && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.color'
                            control={control}
                            render={({ field }) => (
                              <ColorPickerField
                                label='Color'
                                value={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.backgroundColor'
                            control={control}
                            render={({ field }) => (
                              <ColorPickerField
                                label='Background Color'
                                value={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.borderWidth'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Border Width'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.borderColor'
                            control={control}
                            render={({ field }) => (
                              <ColorPickerField
                                label='Border Color'
                                value={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.borderRadius'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Border Radius'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.padding'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Padding'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.margin'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Margin'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.fontSize'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Font Size'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {/* ICON Configuration */}
              {elementType === 'icon' && (
                <>
                  <Grid item xs={12}>
                    <Controller
                      name='isJS'
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={e => field.onChange(e.target.checked)}
                            />
                          }
                          label='JS Icon'
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    {!isJS ? (
                      <Controller
                        name='iconValue'
                        control={control}
                        render={({ field }) => {
                          let iconVal: VectorIcon | null = null

                          if (field.value) {
                            if (typeof field.value === 'object' && 'type' in field.value) {
                              iconVal = field.value as VectorIcon
                            } else if (typeof field.value === 'string') {
                              // Try to parse as VectorIcon or treat as simple string
                              // Use size from initialData if available, otherwise default
                              const defaultSize =
                                (initialData?.type === 'icon'
                                  ? (initialData as IconElement).size
                                  : undefined) || 36
                              iconVal = {
                                type: 'MaterialDesignIcons',
                                value: field.value,
                                size: defaultSize,
                              }
                            }
                          }

                          return (
                            <IconPickerField
                              value={iconVal}
                              onChange={icon => {
                                field.onChange(icon)
                              }}
                              iconRegistry={ICON_REGISTRY}
                            />
                          )
                        }}
                      />
                    ) : (
                      <Box>
                        <Button
                          variant='outlined'
                          fullWidth
                          startIcon={<CodeIcon />}
                          onClick={() => setShowJsEditor(true)}
                        >
                          Edit Icon Script
                        </Button>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name='useDisplayRule'
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={e => field.onChange(e.target.checked)}
                            />
                          }
                          label='Use Display Rule'
                        />
                      )}
                    />

                    {useDisplayRule && (
                      <Controller
                        name='displayRule'
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            value={field.value}
                            onChange={(_, newValue) => field.onChange(newValue)}
                            options={displayRules}
                            getOptionLabel={option => `${option.title} (${option.objectCode})`}
                            loading={isLoadingDisplayRules}
                            renderInput={params => (
                              <TextField
                                {...params}
                                label='Display Rule'
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {isLoadingDisplayRules ? (
                                        <CircularProgress color='inherit' size={20} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name='useStyleParameters'
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={e => field.onChange(e.target.checked)}
                            />
                          }
                          label='Use Style Parameters'
                        />
                      )}
                    />

                    {useStyleParameters && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.color'
                            control={control}
                            render={({ field }) => (
                              <ColorPickerField
                                label='Color'
                                value={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.backgroundColor'
                            control={control}
                            render={({ field }) => (
                              <ColorPickerField
                                label='Background Color'
                                value={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.borderWidth'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Border Width'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.borderColor'
                            control={control}
                            render={({ field }) => (
                              <ColorPickerField
                                label='Border Color'
                                value={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.borderRadius'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Border Radius'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.padding'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Padding'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.margin'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Margin'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.fontSize'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Font Size'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {/* FIELD Configuration */}
              {elementType === 'field' && (
                <>
                  <Grid item xs={12}>
                    <Controller
                      name='title'
                      control={control}
                      render={({ field }) => <TextField {...field} fullWidth label='Title' />}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name='bindType'
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Bind Type</InputLabel>
                          <Select {...field} label='Bind Type'>
                            <MenuItem value='field'>Field</MenuItem>
                            <MenuItem value='js'>JavaScript</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  {bindType === 'field' ? (
                    <>
                      <Grid item xs={12}>
                        <Controller
                          name='selectedObjectCode'
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              value={
                                availableObjects.find(obj => obj.objectCode === field.value) || null
                              }
                              onChange={(_, newValue) => {
                                field.onChange(newValue?.objectCode || null)
                                setValue('field', null) // Reset field when object changes
                                setValue('pathArray', null) // Reset pathArray when object changes
                                if (newValue) {
                                  setValue('objectCode', newValue.objectCode)
                                }
                              }}
                              options={availableObjects}
                              getOptionLabel={option =>
                                `${option.objectTitle} (${option.objectCode})`
                              }
                              isOptionEqualToValue={(option, value) =>
                                option.objectCode === value.objectCode
                              }
                              renderInput={params => (
                                <TextField {...params} label='Object' required />
                              )}
                            />
                          )}
                        />
                      </Grid>

                      {watchObjectCode && (
                        <Grid item xs={12}>
                          <Controller
                            name='pathArray'
                            control={control}
                            rules={{ required: 'Field is required' }}
                            render={({ field, fieldState }) => {
                              const displayPath = watchPathArray
                                ? watchPathArray.map(p => p.field).filter(Boolean).join('.')
                                : ''

                              return (
                                <ModalRefPathPicker
                                  label='Field'
                                  value={
                                    watchPathArray && watchPathArray.length > 0
                                      ? {
                                          path: displayPath,
                                          pathArray: watchPathArray || [],
                                        }
                                      : undefined
                                  }
                                  onChange={next => {
                                    field.onChange(next?.pathArray || null)
                                    // Синхронизируем field из pathArray
                                    if (next?.pathArray && next.pathArray.length > 0) {
                                      const lastPath = next.pathArray[next.pathArray.length - 1]
                                      if (lastPath?.field) {
                                        const foundField = fieldOptions.find(f => f.name === lastPath.field)
                                        if (foundField) {
                                          setValue('field', foundField)
                                          const nameOrCode = foundField.name || (foundField as any).code || ''
                                          setValue('title', nameOrCode)
                                          setValue('code', nameOrCode)
                                          setValue('value', nameOrCode)
                                          setValue('objectCode', watchObjectCode)
                                        }
                                      }
                                    } else {
                                      setValue('field', null)
                                    }
                                  }}
                                  hasClearInInput
                                  isEdit={!!watchPathArray?.length}
                                  isDisabled={false}
                                  isRequired
                                  refPickAllowed
                                  object={
                                    watchObjectCode
                                      ? {
                                          id: watchObjectCode,
                                          label:
                                            availableObjects.find(obj => obj.objectCode === watchObjectCode)
                                              ?.objectTitle || watchObjectCode,
                                        }
                                      : null
                                  }
                                  placeholder='Select field from object'
                                />
                              )
                            }}
                          />
                        </Grid>
                      )}

                      {isDateTimeField && (
                        <Grid item xs={12}>
                          <Controller
                            name='formatDate'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                select
                                label='Date Format'
                                fullWidth
                                value={field.value ?? ''}
                                onChange={event => field.onChange(event.target.value || null)}
                                helperText='Select format for datetime values'
                              >
                                <MenuItem value=''>None</MenuItem>
                                {FORMATS_DATE_OPTIONS.map(option => (
                                  <MenuItem key={option.id} value={option.id}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                          />
                        </Grid>
                      )}
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Button
                        variant='outlined'
                        fullWidth
                        startIcon={<CodeIcon />}
                        onClick={() => setShowJsEditor(true)}
                      >
                        Edit JavaScript Code
                      </Button>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Controller
                      name='useDisplayRule'
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={e => field.onChange(e.target.checked)}
                            />
                          }
                          label='Use Display Rule'
                        />
                      )}
                    />

                    {useDisplayRule && (
                      <Controller
                        name='displayRule'
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            value={field.value}
                            onChange={(_, newValue) => field.onChange(newValue)}
                            options={displayRules}
                            getOptionLabel={option => `${option.title} (${option.objectCode})`}
                            loading={isLoadingDisplayRules}
                            renderInput={params => (
                              <TextField
                                {...params}
                                label='Display Rule'
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {isLoadingDisplayRules ? (
                                        <CircularProgress color='inherit' size={20} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name='useStyleParameters'
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={e => field.onChange(e.target.checked)}
                            />
                          }
                          label='Use Style Parameters'
                        />
                      )}
                    />

                    {useStyleParameters && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.color'
                            control={control}
                            render={({ field }) => (
                              <ColorPickerField
                                label='Color'
                                value={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.backgroundColor'
                            control={control}
                            render={({ field }) => (
                              <ColorPickerField
                                label='Background Color'
                                value={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.borderWidth'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Border Width'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.borderColor'
                            control={control}
                            render={({ field }) => (
                              <ColorPickerField
                                label='Border Color'
                                value={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.borderRadius'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Border Radius'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.padding'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Padding'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.margin'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Margin'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name='styleParameters.fontSize'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label='Font Size'
                                type='number'
                                value={field.value ?? ''}
                                onChange={e =>
                                  field.onChange(
                                    e.target.value === '' ? undefined : e.target.value + 'px'
                                  )
                                }
                                helperText='Value in pixels'
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button type='submit' variant='contained' color='primary'>
              {mode === 'create' ? 'Add' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Script Editor Dialog */}
      {showJsEditor && (
        <ScriptValueDialog
          isShow
          width={800}
          value={
            elementType === 'icon'
              ? typeof iconValue === 'string'
                ? iconValue
                : ''
              : watch('value') || ''
          }
          title={elementType === 'icon' ? 'Edit Icon Script' : 'Edit Field Script'}
          hint={
            elementType === 'icon'
              ? 'Return icon name as string, or icon object with type, value, and size properties.'
              : 'Return the field value based on the current item data.'
          }
          onClose={() => setShowJsEditor(false)}
          onSave={(script: string) => {
            if (elementType === 'icon') {
              setValue('iconValue', script)
            } else {
              setValue('value', script)
            }
            setShowJsEditor(false)
          }}
          language='js'
          variables={templateElementScriptVariables}
          fieldsInfo={variablesToFieldsInfo(templateElementScriptVariables)}
        />
      )}
    </>
  )
}
